import {
  extractGlobalSearchResultPayload,
  hasGlobalSearchReportContent,
} from './globalSearchUi';

/** Data URL или «голый» base64 → только payload base64 (без префикса data:...) */
export function dataUrlToRawBase64(input) {
  if (input == null || typeof input !== 'string') return null;
  const s = input.trim();
  if (!s) return null;
  const m = s.match(/^data:[^;]+;base64,(.+)$/i);
  if (m && m[1]) return m[1].trim() || null;
  if (/^[A-Za-z0-9+/]+=*$/.test(s) && s.length >= 32) return s;
  return null;
}

function normalizeItems(raw) {
  if (raw === '' || raw == null) return [];
  if (!Array.isArray(raw)) return [];
  return raw;
}

function docFromGridItem(item) {
  if (!item || typeof item !== 'object') return null;
  return item.document ?? item;
}

/**
 * Скаляр для карточки: массив → только элемент с индексом 0, без JSON.stringify всего массива.
 */
function firstCellValue(v) {
  if (v == null) return null;
  let x = v;
  if (Array.isArray(x)) {
    x = x[0];
    if (x == null) return null;
  }
  if (typeof x === 'object') return JSON.stringify(x);
  const s = String(x).trim();
  return s === '' ? null : s;
}

function pickTzEntry(doc) {
  if (!doc) return { date: null, number: null };
  return {
    date: firstCellValue(doc['450_dataPublic']),
    number: firstCellValue(doc['111_numReg']),
  };
}

function pickReqEntry(doc) {
  if (!doc) return { date: null, number: null };
  return {
    date: firstCellValue(doc['Дата_из_таблицы']),
    number: firstCellValue(doc['210_Номер_заявки']),
  };
}

function pickWipoEntry(doc) {
  if (!doc) return { date: null, number: null };
  return {
    date: firstCellValue(doc['Reg. Date'] ?? doc['MARKGR@INTREGD']),
    number: firstCellValue(
      doc['Reg. No'] ?? doc['@INTREGN'] ?? doc['Регистрационный номер'],
    ),
  };
}

function mapSubItems(items, subId) {
  const picker =
    subId === 'tz' ? pickTzEntry : subId === 'req' ? pickReqEntry : pickWipoEntry;
  return normalizeItems(items).map((item) => picker(docFromGridItem(item)));
}

function normalizeMktuOut(mktu) {
  if (mktu == null || mktu === '') return [];
  if (Array.isArray(mktu)) return mktu.map((x) => String(x));
  return [String(mktu)];
}

/**
 * @param {{ formKey: string, queryKey: string }} def
 * @param {object} tz
 */
function buildTabSearch(def, tz) {
  const slice = tz?.forms?.[def.formKey];
  const base = { query: null, quantity: null, mktu: [] };
  if (!slice || typeof slice !== 'object') return base;

  if (def.formKey === 'konstruktor') {
    const q =
      slice.text != null && String(slice.text).trim() !== '' ? String(slice.text) : null;
    return {
      query: q,
      quantity: slice.quantity ?? null,
      mktu: normalizeMktuOut(slice.mktu),
      scope: slice.scope ?? null,
      selected_fields: Array.isArray(slice.selectedFields) ? [...slice.selectedFields] : [],
      filters: Array.isArray(slice.filters)
        ? slice.filters.map((f) => (f && typeof f === 'object' ? { ...f } : f))
        : [],
      is_phonetic: Boolean(slice.isPhonetic),
    };
  }

  const rawQ = slice[def.queryKey];
  let query =
    rawQ != null && String(rawQ).trim() !== '' ? String(rawQ) : null;
  if (!query && def.formKey === 'image') {
    const fn = slice.fileName;
    query =
      fn != null && String(fn).trim() !== '' ? String(fn) : null;
  }

  const out = {
    query,
    quantity: slice.quantity ?? null,
    mktu: normalizeMktuOut(slice.mktu),
  };

  if (def.formKey === 'image') {
    const b64 = dataUrlToRawBase64(tz?.lastImageForReport);
    if (b64) out.image_base64 = b64;
  }

  if (def.formKey === 'description') {
    const b64 = dataUrlToRawBase64(tz?.lastDescriptionSearchImageDataUrl);
    if (b64) out.image_base64 = b64;
  }

  return out;
}

/**
 * Описание верхнеуровневых режимов поиска (как вкладки в TZ.jsx) и источников в Redux.
 * formKey / queryKey — фрагмент формы в tz.forms (Redux).
 * subs: id — ключ в JSON (tz | req | wipo), requestType — request_type на сервере и ключ в search.results.
 */
const SEARCH_TAB_DEFINITIONS = [
  {
    label: 'По названию',
    formKey: 'name',
    queryKey: 'name',
    subs: [
      { id: 'tz', requestType: 'name' },
      { id: 'req', requestType: 'requests' },
      { id: 'wipo', requestType: 'wipoName' },
    ],
  },
  {
    label: 'По содержанию',
    formKey: 'description',
    queryKey: 'description',
    subs: [
      { id: 'tz', requestType: 'description_tz' },
      { id: 'req', requestType: 'description_req' },
      { id: 'wipo', requestType: 'description_wipo' },
    ],
  },
  {
    label: 'По адресу',
    formKey: 'adress',
    queryKey: 'adress',
    subs: [
      { id: 'tz', requestType: 'adress' },
      { id: 'req', requestType: 'adressReq' },
      { id: 'wipo', requestType: 'wipoAdres' },
    ],
  },
  {
    label: 'По правообладателю',
    formKey: 'patentObl',
    queryKey: 'patentObl',
    subs: [
      { id: 'tz', requestType: 'patentObl' },
      { id: 'req', requestType: 'patentOblRequests' },
      { id: 'wipo', requestType: 'wipoObl' },
    ],
  },
  {
    label: 'По изображению',
    formKey: 'image',
    queryKey: 'fileName',
    subs: [
      { id: 'tz', requestType: 'image' },
      { id: 'req', requestType: 'imageReq' },
      { id: 'wipo', requestType: 'imageWipo' },
    ],
  },
  {
    label: 'Конструктор',
    formKey: 'konstruktor',
    queryKey: 'text',
    subs: [
      { id: 'tz', requestType: 'konstruktor' },
      { id: 'req', requestType: 'konstruktor_req' },
      { id: 'wipo', requestType: 'konstruktor_wipo' },
    ],
  },
];

function getRawItems(search, sub) {
  return search?.results?.[sub.requestType];
}

/**
 * @param {{ tz: object, search: object, globalSearch: object }} stateSlice
 * @returns {Record<string, object>}
 */
export function collectSearchTabsSnapshot({ tz, search, globalSearch }) {
  const out = {};

  for (const tab of SEARCH_TAB_DEFINITIONS) {
    const tabData = {};
    for (const sub of tab.subs) {
      const raw = getRawItems(search, sub);
      const list = normalizeItems(raw);
      if (list.length === 0) continue;
      tabData[sub.id] = mapSubItems(list, sub.id);
    }
    if (Object.keys(tabData).length > 0) {
      out[tab.label] = {
        search: buildTabSearch(tab, tz),
        ...tabData,
      };
    }
  }

  const gPayload = extractGlobalSearchResultPayload(globalSearch?.result);
  if (hasGlobalSearchReportContent(gPayload)) {
    const globalBlock = {};
    const hits = Array.isArray(gPayload.yandexSearch) ? gPayload.yandexSearch : [];
    if (hits.length > 0) {
      globalBlock.yandex_hits = hits.map((h) => ({
        title: h.title ?? null,
        pageUrl: h.pageUrl ?? null,
        score: typeof h.score === 'number' && !Number.isNaN(h.score) ? h.score : null,
      }));
    }
    if (Array.isArray(gPayload.yandexUrls) && gPayload.yandexUrls.length > 0) {
      globalBlock.yandex_urls_legacy = gPayload.yandexUrls.map((u) =>
        typeof u === 'string' ? u : u?.url ?? u?.href ?? String(u),
      );
    }
    if (gPayload.similarities != null) {
      globalBlock.similarities_legacy = gPayload.similarities;
    }
    if (Object.keys(globalBlock).length > 0) {
      const gSearch = {
        query: globalSearch?.lastSubmittedFileName || null,
        quantity: null,
        mktu: [],
      };
      const gb64 = globalSearch?.lastSubmittedImageBase64;
      if (gb64 != null && String(gb64).trim() !== '') {
        gSearch.image_base64 = String(gb64).trim();
      }
      out['Глобальный поиск'] = {
        search: gSearch,
        ...globalBlock,
      };
    }
  }

  return out;
}

export function hasSearchSnapshotContent(snapshot) {
  return snapshot && typeof snapshot === 'object' && Object.keys(snapshot).length > 0;
}
